/**
 * TASK-301: Factures Récurrentes - Routes
 * Routes pour la gestion des templates de facturation récurrente
 *
 * Refactored: useRecurringInvoice(id) hook, shared formatCurrency/formatDate
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, CreditCard, FileText, Zap } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/vatCalculations';
import { useRecurringInvoice } from '../hooks/useRecurringInvoices';
import { RecurringInvoicesList } from '../components/RecurringInvoicesList';
import { RecurringInvoiceForm } from '../components/RecurringInvoiceForm';
import { Button, Spinner } from '@/components/ui-v2';

// Liste des factures récurrentes
export const RecurringInvoicesListRoute: React.FC = () => {
  return <RecurringInvoicesList />;
};

// Vue d'un template en détail (F3 fix: useRecurringInvoice hook)
export const RecurringInvoiceViewRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: template, isLoading: loading, error } = useRecurringInvoice(id);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-8">
          <div className="flex items-center gap-3">
            <Spinner size="md" />
            <p className="text-gray-700 font-medium">{t('templates.common.loadingTemplate')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <p className="text-red-600 font-semibold mb-4">{t('billing.recurring.templateNotFound')}</p>
          <Button onClick={() => navigate("/recurring-invoices")}>
            {t('common.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  const FREQUENCY_LABELS: Record<string, string> = {
    WEEKLY: t('billing.frequency.WEEKLY'),
    BIWEEKLY: t('billing.frequency.BIWEEKLY'),
    MONTHLY: t('billing.frequency.MONTHLY'),
    BIMONTHLY: t('billing.frequency.BIMONTHLY'),
    QUARTERLY: t('billing.frequency.QUARTERLY'),
    SEMIANNUAL: t('billing.frequency.SEMIANNUAL'),
    ANNUAL: t('billing.frequency.ANNUAL'),
  };

  const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    ACTIVE: { label: t('billing.recurringStatus.ACTIVE'), className: 'bg-teal-50 text-teal-700' },
    PAUSED: { label: t('billing.recurringStatus.PAUSED'), className: 'bg-amber-50 text-amber-700' },
    COMPLETED: { label: t('billing.recurringStatus.COMPLETED'), className: 'bg-gray-100 text-gray-600' },
    CANCELLED: { label: t('billing.recurringStatus.CANCELLED'), className: 'bg-red-50 text-red-600' },
  };

  const statusConfig = STATUS_CONFIG[template.status] || STATUS_CONFIG.ACTIVE;

  return (
    <div className="px-8 pt-8 pb-12 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <button
            onClick={() => navigate("/recurring-invoices")}
            className="text-sm text-gray-500 hover:text-gray-700 mb-3 flex items-center gap-1 transition-colors"
          >
            {t('common.backToList')}
          </button>
          <h1 className="text-3xl font-light tracking-tight text-gray-900">{template.name}</h1>
          {template.description && (
            <p className="text-gray-500 mt-1">{template.description}</p>
          )}
          <span className={`inline-block mt-3 px-3 py-1 rounded-full text-sm font-medium ${statusConfig.className}`}>
            {statusConfig.label}
          </span>
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate(`/recurring-invoices/${id}/edit`)}
          disabled={template.status === 'CANCELLED'}
        >
          {t('common.actions.edit')}
        </Button>
      </div>

      {/* Info Card */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-6 space-y-6">
        {/* Client */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">{t('billing.fields.client')}</h3>
          <p className="text-gray-900 font-medium text-lg">
            {template.Client?.companyName || template.Client?.fullName || '-'}
          </p>
          {template.Client?.email && (
            <p className="text-sm text-gray-500">{template.Client.email}</p>
          )}
        </div>

        {/* Détails */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-white/30">
          <div>
            <h3 className="text-sm font-medium text-gray-500">{t('billing.fields.frequency')}</h3>
            <p className="text-gray-900 font-medium">{FREQUENCY_LABELS[template.frequency] || template.frequency}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">{t('billing.recurring.paymentTermsDays')}</h3>
            <p className="text-gray-900 font-medium">{template.paymentTermsDays} jours</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">{t('billing.recurring.generatedInvoices')}</h3>
            <p className="text-gray-900 font-medium">{template.generatedCount}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">{t('billing.recurring.autoSend')}</h3>
            <p className="text-gray-900 font-medium">{template.autoSend ? t('common.labels.enabled') : t('common.labels.disabled')}</p>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-white/30">
          <div>
            <h3 className="text-sm font-medium text-gray-500">{t('billing.fields.startDate')}</h3>
            <p className="text-gray-900 font-medium">{formatDate(template.startDate)}</p>
          </div>
          {template.endDate && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">{t('billing.fields.endDate')}</h3>
              <p className="text-gray-900 font-medium">{formatDate(template.endDate)}</p>
            </div>
          )}
          <div>
            <h3 className="text-sm font-medium text-gray-500">{t('billing.recurring.nextInvoice')}</h3>
            <p className="text-teal-600 font-semibold">{formatDate(template.nextInvoiceDate)}</p>
          </div>
        </div>

        {/* Lignes du template */}
        {template.templateLines && template.templateLines.length > 0 && (
          <div className="pt-4 border-t border-white/30">
            <h3 className="text-sm font-medium text-gray-500 mb-3">{t('billing.recurring.templateLines')}</h3>
            <div className="space-y-2">
              {template.templateLines.map((line, index) => (
                <div key={index} className="flex justify-between items-center py-3 px-4 bg-white/40 border border-white/50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">{line.description}</p>
                    <p className="text-sm text-gray-500">
                      {line.quantity} × {formatCurrency(line.unitPrice)} (TVA {line.taxRate}%)
                    </p>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {formatCurrency(line.quantity * line.unitPrice * (1 + line.taxRate / 100))}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Montants */}
        <div className="pt-4 border-t border-white/30">
          <div className="flex flex-col items-end gap-2">
            <div className="flex justify-between w-56">
              <span className="text-gray-500">{t('templates.common.subtotalHT')}</span>
              <span className="font-medium text-gray-900">{formatCurrency(template.totalHT)}</span>
            </div>
            <div className="flex justify-between w-56">
              <span className="text-gray-500">{t('templates.common.vat')}</span>
              <span className="font-medium text-gray-900">{formatCurrency(template.totalTVA)}</span>
            </div>
            <div className="flex justify-between w-56 pt-2 border-t border-gray-200">
              <span className="font-semibold text-gray-900">{t('templates.common.totalTTC')}</span>
              <span className="font-bold text-2xl text-teal-600">{formatCurrency(template.totalTTC)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {template.notes && (
          <div className="pt-4 border-t border-white/30">
            <h3 className="text-sm font-medium text-gray-500 mb-2">{t('billing.recurring.internalNotes')}</h3>
            <p className="text-gray-700 whitespace-pre-wrap bg-white/40 rounded-xl p-4">{template.notes}</p>
          </div>
        )}

        {/* Conditions */}
        {template.termsConditions && (
          <div className="pt-4 border-t border-white/30">
            <h3 className="text-sm font-medium text-gray-500 mb-2">{t('billing.recurring.paymentTerms')}</h3>
            <p className="text-gray-700 whitespace-pre-wrap bg-white/40 rounded-xl p-4">{template.termsConditions}</p>
          </div>
        )}

        {/* Payment Mandate */}
        <div className="pt-4 border-t border-white/30">
          <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            {t('billing.recurring.autoPayment')}
          </h3>

          {template.autoPayment && template.PaymentMandate ? (
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 p-4 flex items-center gap-4">
              <div className={`p-3 rounded-xl ${
                template.PaymentMandate.provider === 'GOCARDLESS'
                  ? 'text-blue-600 bg-blue-50'
                  : template.PaymentMandate.provider === 'STRIPE'
                  ? 'text-purple-600 bg-purple-50'
                  : 'text-gray-600 bg-gray-50'
              }`}>
                {template.PaymentMandate.provider === 'GOCARDLESS' ? (
                  <Building2 className="w-5 h-5" />
                ) : template.PaymentMandate.provider === 'STRIPE' ? (
                  <CreditCard className="w-5 h-5" />
                ) : (
                  <FileText className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">
                  {template.PaymentMandate.ibanLast4
                    ? `**** **** **** ${template.PaymentMandate.ibanLast4}`
                    : template.PaymentMandate.cardLast4
                    ? `**** ${template.PaymentMandate.cardLast4}`
                    : template.PaymentMandate.accountHolderName || 'Paiement manuel'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    template.PaymentMandate.provider === 'GOCARDLESS'
                      ? 'text-blue-600 bg-blue-50'
                      : template.PaymentMandate.provider === 'STRIPE'
                      ? 'text-purple-600 bg-purple-50'
                      : 'text-gray-600 bg-gray-50'
                  }`}>
                    {template.PaymentMandate.provider === 'GOCARDLESS' ? 'SEPA' : template.PaymentMandate.provider === 'STRIPE' ? 'Carte' : 'Manuel'}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    template.PaymentMandate.status === 'ACTIVE'
                      ? 'text-emerald-600 bg-emerald-50'
                      : 'text-amber-600 bg-amber-50'
                  }`}>
                    {template.PaymentMandate.status === 'ACTIVE' ? 'Actif' : template.PaymentMandate.status}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-sm italic">
              {template.autoPayment ? 'Mandat non configure' : 'Desactive'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Création d'un nouveau template
export const RecurringInvoiceNewRoute: React.FC = () => {
  return <RecurringInvoiceForm />;
};

// Edition d'un template (F3 fix: useRecurringInvoice hook)
export const RecurringInvoiceEditRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t: tEdit } = useTranslation();
  const { data: template, isLoading: loading, error } = useRecurringInvoice(id);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-8">
          <div className="flex items-center gap-3">
            <Spinner size="md" />
            <p className="text-gray-700 font-medium">{tEdit('templates.common.loadingTemplate')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <p className="text-red-600 font-semibold mb-4">{tEdit('billing.recurring.templateNotFound')}</p>
          <Button onClick={() => navigate("/recurring-invoices")}>
            {tEdit('common.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  return <RecurringInvoiceForm template={template} />;
};
